/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import './ActivitySummary.scss';
import { Spinner } from '@trp-beacon-ui-kit/react-communication';

import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import { getAccountActivity, getCommentary } from 'services/Rest';
import Content from 'services/Content';

import ActivitySummaryCommentary from './ActivitySummaryCommentary';
import ActivitySummaryChart from './ActivitySummaryChart';
import { getActivitySummaryCommentary } from 'state/commentary/CommentarySelectors';
import { ACTIVITY_SUMMARY_TYPE } from 'services/Commentary';
import {
  getAccountsForActivitySummary,
  getActivitySummaryData,
  getActivitySummaryFilterIsNull,
  getDateRangeForActivitySummary
} from 'state/performance/ActivitySummaryFilterSelectors';
import {getIsRequestingActivitySummaryData} from 'state/requests/RequestStatusSelectors';
import { isValidPerformanceConstraint } from 'state/performance/PerformancePageUtils';
import { getHasPershingAccounts } from 'state/account/AccountSelectors';
import { withOtelContext } from '@trp-ta-nitro/otel-react';
import otapi from '@opentelemetry/api';
import {addActivitySummaryTableErrorScreenEnabled} from 'services/FeatureFlags';
import PerformanceWarningMessage from 'components/layouts/Performance/PerformanceWarningMessage.js';

const ActivitySummary = ({
  commentary,
  summary,
  accounts,
  dateRange,
  hasPershingAccounts,
  otelContext,
  isRequesting,
  isFailed,
}) => {
  useEffect(() => {
    getCommentary(ACTIVITY_SUMMARY_TYPE).catch((e) => {
      console.log('Error retreiving summary type', e.getMessage());
    });
  }, []);

  useEffect(() => {
    if (
      isValidPerformanceConstraint(
        accounts,
        dateRange.fromDate,
        dateRange.toDate
      )
    ) {
      otapi.context.with(otelContext, () => {
        getAccountActivity(dateRange.fromDate, dateRange.toDate, accounts);
      });
    }
  }, [dateRange.fromDate, dateRange.toDate, accounts, otelContext]);

  const activitySummaryErrorMessage = addActivitySummaryTableErrorScreenEnabled() && isFailed;

  return (
    <div className="activity-summary module module-container">
      <div className="activity-chart">
        {isRequesting ? (
          <Spinner />
        ) : activitySummaryErrorMessage ? (
          <PerformanceWarningMessage
            message={Content.activitySummaryWarningCallFailed}
          />
          ) : (
            <ActivitySummaryChart
              summary={summary}
              hasPershingAccounts={hasPershingAccounts}
            />
          )}
      </div>
      <div className="commentary-disclosure-container">
        <div className="commentary-sidebar">
          <ActivitySummaryCommentary
            commentary={commentary}
            summary={summary}
          />
          <div className="see-table activity-summary">
            To see the accounts included in the analysis, refer to the “View
            Activity Summary Breakdown” table below.
          </div>
          <div className="purged-account-copy activity-summary">
            Closed accounts are not included.
          </div>
          <DisclosureModal
            className="activity-summary"
            content={
              hasPershingAccounts
                ? Content.activitySummaryDisclosureWithAdjustments
                : Content.activitySummaryDisclosure
            }
            iconAfter
          />
        </div>
      </div>
    </div>
  );
};

ActivitySummary.propTypes = {
  commentary: PropTypes.object
};

const mapStateToProps = (state) => {
  return {
    commentary: getActivitySummaryCommentary(state),
    summary: getActivitySummaryData(state),
    accounts: getAccountsForActivitySummary(state),
    dateRange: getDateRangeForActivitySummary(state),
    hasPershingAccounts: getHasPershingAccounts(state),
    isRequesting: getIsRequestingActivitySummaryData(state),
    isFailed: getActivitySummaryFilterIsNull(state),
  };
};

export default compose(
  withOtelContext,
  connect(mapStateToProps)
)(ActivitySummary);
